// Layout principal para páginas autenticadas
// Incluye navbar, sidebar y footer consistentes
// Maneja la actualización de ubicación del usuario con Explorer
// Detecta dispositivo y renderiza DesktopLayout o MobileLayout según corresponda

import React, { useEffect, useState, useCallback, memo } from 'react';
import { useAuth } from '../../hooks/useAuth';
import {
  getLocationState,
  getCurrentDeviceLocation,
  getActiveLocation,
  syncUserGeo,
  disableExplorerMode,
  isGeolocationSupported,
  getGeolocationPermissionStatus
} from '../../hooks/location/useLocationState';
import DesktopLayout from './DesktopLayout';
import MobileLayout from './MobileLayout';

/**
 * Componente MainLayout
 * 
 * Layout principal para usuarios autenticados
 * Detecta tipo de dispositivo (desktop/mobile) y renderiza el layout correspondiente
 * Maneja la actualización periódica de ubicación del usuario con soporte para Explorer
 * 
 * @param {Object} props - Props del componente
 * @param {React.ReactNode} props.children - Contenido de la página
 * @param {boolean} props.hideNavbar - Ocultar Navbar en mobile (default: false)
 * @param {boolean} props.hideNavbarBottom - Ocultar NavbarBottom en mobile (default: false)
 */
const MainLayout = ({ children, hideNavbar = false, hideNavbarBottom = false }) => {
  const { user } = useAuth();
  const [location, setLocation] = useState(null);
  const [error, setError] = useState(null);
  const [showPermissionAlert, setShowPermissionAlert] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState('unknown');
  
  // Detectar si es dispositivo móvil
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
      const isMobileDevice = mobileRegex.test(userAgent) || window.innerWidth < 768;
      setIsMobile(isMobileDevice);
    };
    
    checkMobile();
    
    // Escuchar cambios de tamaño de ventana
    const handleResize = () => {
      checkMobile();
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Verificar estado de permisos de geolocalización al montar
  useEffect(() => {
    const checkPermissionStatus = async () => {
      const supported = isGeolocationSupported();
      if (!supported) {
        setPermissionStatus('unsupported');
        return;
      }
      
      try {
        const status = await getGeolocationPermissionStatus();
        setPermissionStatus(status);
        console.log('[MainLayout] Permission status:', status);
      } catch (e) {
        console.error('[MainLayout] Error checking permission status:', e);
        setPermissionStatus('unknown');
      }
    };
    
    checkPermissionStatus();
  }, []);
  
  // Inicializar ubicación al montar
  useEffect(() => {
    if (!user?.id) return;
    
    const initializeLocation = async () => {
      try {
        const state = getLocationState();
        
        if (state?.explorerActive) {
          // Caso 1: Explorer activo
          console.log('[MainLayout] Explorer mode active, using explorer location');
          const activeLoc = getActiveLocation();
          setLocation(activeLoc);
          await syncUserGeo(activeLoc, user.id);
        } else {
          // Caso 2: Explorer desactivado
          console.log('[MainLayout] Explorer mode inactive, using device location');
          const deviceLoc = await getCurrentDeviceLocation();
          setLocation(deviceLoc);
          await syncUserGeo(deviceLoc, user.id);
        }
      } catch (e) {
        console.error('[MainLayout] Error initializing location:', e);
        if (e.name === 'GeolocationPositionError') {
          setShowPermissionAlert(true);
        } else {
          setError('Error al inicializar ubicación');
        }
      }
    };
    
    initializeLocation();
  }, [user?.id]);
  
  // Configurar actualización periódica SOLO si Explorer está desactivado
  useEffect(() => {
    if (!user?.id) return;
    
    const state = getLocationState();
    if (state?.explorerActive) {
      console.log('[MainLayout] Explorer active, skipping periodic updates');
      return;
    }
    
    const intervalId = setInterval(async () => {
      console.log('[MainLayout] Periodic location update');
      try {
        const deviceLoc = await getCurrentDeviceLocation();
        setLocation(deviceLoc);
        await syncUserGeo(deviceLoc, user.id);
      } catch (e) {
        console.error('[MainLayout] Error in periodic update:', e);
      }
    }, 5 * 60 * 1000); // 5 minutos
    
    return () => clearInterval(intervalId);
  }, [user?.id]);
  
  const requestLocationPermission = useCallback(async () => {
    setShowPermissionAlert(false);
    setLoading(true);
    setError(null);
    
    try {
      // Verificar soporte primero
      const supported = isGeolocationSupported();
      if (!supported) {
        throw new Error('Tu navegador no soporta geolocalización');
      }
      
      // Forzar actualización de ubicación (sin cache)
      // Esto debería activar el prompt nativo del navegador
      const deviceLoc = await getCurrentDeviceLocation(true);
      setLocation(deviceLoc);
      await syncUserGeo(deviceLoc, user.id);
      
      // Actualizar estado de permisos
      const newStatus = await getGeolocationPermissionStatus();
      setPermissionStatus(newStatus);
      
    } catch (e) {
      console.error('[MainLayout] Error requesting permission:', e);
      
      // Manejar diferentes tipos de errores
      if (e.name === 'GeolocationPositionError') {
        if (e.code === e.PERMISSION_DENIED) {
          setError('Permiso de ubicación denegado. Por favor habilita la geolocalización en la configuración de tu navegador.');
          setPermissionStatus('denied');
        } else if (e.code === e.POSITION_UNAVAILABLE) {
          setError('No se pudo determinar tu ubicación. Intenta nuevamente.');
        } else if (e.code === e.TIMEOUT) {
          setError('Tiempo de espera agotado al obtener ubicación. Intenta nuevamente.');
        } else {
          setError('Error al obtener ubicación. Intenta nuevamente.');
        }
      } else {
        setError(e.message || 'Error al obtener ubicación');
      }
      
      // Mostrar alert nuevamente si es un error de permisos
      if (e.name === 'GeolocationPositionError' && e.code === e.PERMISSION_DENIED) {
        setShowPermissionAlert(true);
      }
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  const handleDismissAlert = useCallback(() => {
    setShowPermissionAlert(false);
  }, []);

  // Renderizar layout según tipo de dispositivo
  const LayoutComponent = isMobile ? MobileLayout : DesktopLayout;

  // Formatear la ubicación para mostrarla al usuario
  const formatLocation = (loc) => {
    if (!loc) return 'Ubicación no disponible';
    return `${loc.lat.toFixed(4)}, ${loc.lng.toFixed(4)}`;
  };

  // Determinar si mostrar alert de permisos
  const shouldShowPermissionAlert = showPermissionAlert || permissionStatus === 'denied' || permissionStatus === 'unsupported';

  return (
    <div className="relative">
      {/* Header con ubicación */}
      {false && <div className="flex items-center justify-between p-4 bg-white dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700">
        <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">Amarsi</h1>
        
        {/* Mostrar ubicación del usuario */}
        <div className="flex items-center space-x-2">
          <span className="text-sm text-neutral-600 dark:text-neutral-400">
            Ubicación: {formatLocation(location)}
          </span>
          {loading && (
            <span className="text-xs text-neutral-500 dark:text-neutral-500">Actualizando...</span>
          )}
        </div>
      </div>}

      {/* Modal de permisos de geolocalización - diseño moderno y distintivo */}
      {shouldShowPermissionAlert && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/95 backdrop-blur-xl">
          <div className="relative max-w-lg w-full mx-4">
            {/* Decoración de fondo */}
            <div className="absolute -inset-1 bg-neutral-950 rounded-2xl blur-lg opacity-75"></div>
            
            {/* Contenedor principal */}
            <div className="relative bg-white dark:bg-neutral-900 rounded-2xl shadow-2xl overflow-hidden">
              {/* Header del modal */}
              <div className="bg-gray-600 p-6 text-white">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                    {permissionStatus === 'unsupported' ? (
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    ) : permissionStatus === 'denied' ? (
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    ) : (
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    )}
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">
                      {permissionStatus === 'unsupported' 
                        ? 'Geolocalización no disponible' 
                        : permissionStatus === 'denied'
                        ? 'Acceso a ubicación denegado'
                        : 'Permite tu ubicación'}
                    </h2>
                    <p className="text-white/80 text-sm mt-1">
                      {permissionStatus === 'unsupported' 
                        ? 'Tu navegador actual no soporta esta función'
                        : permissionStatus === 'denied'
                        ? 'Necesitamos tu ubicación para continuar'
                        : 'Amarsi necesita saber dónde estás'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Contenido del modal */}
              <div className="p-6 space-y-4">
                <p className="text-neutral-700 dark:text-neutral-300 leading-relaxed">
                  {permissionStatus === 'unsupported'
                    ? 'Tu navegador no soporta geolocalización. Por favor usa un navegador moderno como Chrome, Safari o Firefox para acceder a todas las funciones de Amarsi.'
                    : permissionStatus === 'denied'
                    ? 'Amarsi necesita este permiso para mostrarte contenido relevante y conectarte con personas cercanas. Por favor, habilita la geolocalización en tu navegador.'
                    : 'Para ofrecerte la mejor experiencia, Amarsi necesita acceder a tu ubicación. Esto nos permite mostrar contenido relevante y conectarte con personas en tu área.'}
                </p>

                {permissionStatus !== 'unsupported' && (
                  <button
                    onClick={requestLocationPermission}
                    disabled={loading}
                    className="w-full py-3 px-6 bg-gray-600 text-white font-semibold rounded-xl shadow-lg hover:bg-gray-700 hover:shadow-xl transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center space-x-2">
                        <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Solicitando permiso...</span>
                      </span>
                    ) : permissionStatus === 'denied' ? (
                      'Intentar nuevamente'
                    ) : (
                      'Permitir acceso a mi ubicación'
                    )}
                  </button>
                )}
                
                {permissionStatus !== 'denied' && (
                  <button
                    onClick={handleDismissAlert}
                    className="w-full py-2 px-6 text-neutral-600 dark:text-neutral-400 hover:text-neutral-800 dark:hover:text-neutral-200 font-medium transition-colors"
                  >
                    Cancelar
                  </button>
                )}
                
                {permissionStatus === 'denied' && (
                  <div className="mt-4 p-4 bg-neutral-50 dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700">
                    <p className="font-semibold text-neutral-900 dark:text-white mb-2 flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Cómo habilitar ubicación:
                    </p>
                    <ul className="space-y-2 text-sm text-neutral-600 dark:text-neutral-400">
                      <li className="flex items-start">
                        <span className="mr-2">🍎</span>
                        <span><strong>Safari:</strong> Configuración → Privacidad → Ubicación → Habilitar</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">🌐</span>
                        <span><strong>Chrome:</strong> Configuración → Privacidad → Ubicación → Permitir</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">📱</span>
                        <span><strong>Chrome iOS:</strong> Configuración → Chrome → Ubicación → Mientras usas la app</span>
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Alert de error general - diseño moderno */}
      {error && !location && (
        <div className="max-w-7xl mx-auto mt-4 px-4">
          <div className="relative bg-white dark:bg-neutral-900 rounded-xl shadow-lg overflow-hidden border-l-4 border-gray-500">
            <div className="p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-gray-100 dark:bg-gray-900/30 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
                    Error de ubicación
                  </h3>
                  <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
                    {error}
                  </p>
                  <button
                    onClick={() => setError(null)}
                    className="mt-3 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300 transition-colors"
                  >
                    Entendido
                  </button>
                </div>
                <button
                  onClick={() => setError(null)}
                  className="ml-4 flex-shrink-0 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Solo renderizar el contenido principal si hay ubicación */}
      {location && (
        <LayoutComponent
          children={children}
          locationData={location}
          requestLocationPermission={requestLocationPermission}
          showPermissionAlert={showPermissionAlert}
          loading={loading}
          onDismissAlert={handleDismissAlert}
          hideNavbar={hideNavbar}
          hideNavbarBottom={hideNavbarBottom}
        />
      )}
    </div>
  );
};

// Memoizar MainLayout para prevenir re-renders innecesarios
// Solo se re-renderiza si children cambia
export default memo(MainLayout, (prevProps, nextProps) => {
  return prevProps.children === nextProps.children;
});
